import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
	ArrowRight,
	ArrowUpRight,
	CalendarDays,
	CheckCircle2,
	Clock3,
	Coffee,
	Mail,
	Package,
	Plus,
	RefreshCw,
	Settings2,
	ShoppingBag,
	TrendingUp,
	Users,
	Wallet,
	AlertCircle,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { getAdminStats } from "../../services/adminService";

const formatCurrency = (value) =>
	new Intl.NumberFormat("fr-CH", {
		style: "currency",
		currency: "CHF",
	}).format(Number(value) || 0);

const formatNumber = (value) =>
	new Intl.NumberFormat("fr-CH").format(Number(value) || 0);

const quickLinks = [
	{
		title: "Commandes",
		description: "Consulter et gérer les commandes",
		to: "/admin/orders",
		icon: ShoppingBag,
	},
	{
		title: "Produits",
		description: "Gérer le catalogue et les stocks",
		to: "/admin/products",
		icon: Package,
	},
	{
		title: "Ajouter un produit",
		description: "Créer une nouvelle fiche produit",
		to: "/admin/products/new",
		icon: Plus,
	},
	{
		title: "Clients",
		description: "Gérer les comptes utilisateurs",
		to: "/admin/users",
		icon: Users,
	},
	{
		title: "Réservations",
		description: "Consulter les réservations",
		to: "/admin/reservations",
		icon: CalendarDays,
	},
	{
		title: "Newsletter",
		description: "Gérer les communications",
		to: "/admin/newsletter",
		icon: Mail,
	},
	{
		title: "Paramètres",
		description: "Configurer votre boutique",
		to: "/admin/settings",
		icon: Settings2,
	},
];

const StatCard = ({ title, value, icon: Icon, description, loading }) => (
	<div className="group relative overflow-hidden rounded-[24px] border border-[#E9E2D8] bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-6">
		<div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-[#F8F1E7] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

		<div className="relative flex items-start justify-between gap-3">
			<div className="flex-1">
				<p className="text-sm font-medium text-[#82766B]">{title}</p>

				{loading ? (
					<div className="mt-4 h-9 w-28 animate-pulse rounded-lg bg-[#F0EBE5]" />
				) : (
					<p className="mt-3 break-words text-2xl font-bold tracking-tight text-[#30251E] sm:text-3xl">
						{value}
					</p>
				)}
			</div>

			<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F6EEE2] text-[#A27B4C]">
				<Icon size={23} strokeWidth={1.8} />
			</div>
		</div>

		<div className="relative mt-5 flex items-center gap-2 border-t border-[#F0EBE5] pt-4">
			<span className="h-2 w-2 rounded-full bg-[#C6A675]" />
			<span className="text-xs text-[#93877C]">{description}</span>
		</div>
	</div>
);

const QuickLinkCard = ({ title, description, to, icon: Icon }) => (
	<Link
		to={to}
		className="group flex items-center gap-4 rounded-2xl border border-[#EEE8DF] bg-white p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D9C6AA] hover:bg-[#FFFCF8] hover:shadow-md sm:p-5"
	>
		<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F7F2EB] text-[#9E794B] transition-colors group-hover:bg-[#30251E] group-hover:text-white">
			<Icon size={22} strokeWidth={1.8} />
		</div>

		<div className="min-w-0 flex-1">
			<h3 className="font-semibold text-[#30251E]">{title}</h3>
			<p className="mt-1 text-xs leading-relaxed text-[#91867B] sm:text-sm">
				{description}
			</p>
		</div>

		<ArrowUpRight
			size={19}
			className="shrink-0 text-[#B4A99C] transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#9E794B]"
		/>
	</Link>
);

const AdminDashboardPage = () => {
	const { profile } = useAuth();

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState("");

	const loadStats = useCallback(async (isRefresh = false) => {
		if (isRefresh) {
			setRefreshing(true);
		} else {
			setLoading(true);
		}

		setError("");

		try {
			const result = await getAdminStats();
			setData(result);
		} catch (err) {
			console.error("Erreur chargement dashboard admin:", err?.message || err);

			setError("Impossible de récupérer les statistiques. Veuillez réessayer.");
		} finally {
			setLoading(false);
			setRefreshing(false);
		}
	}, []);

	useEffect(() => {
		loadStats();
	}, [loadStats]);

	const today = new Intl.DateTimeFormat("fr-CH", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(new Date());

	const stats = [
		{
			title: "Commandes du jour",
			value: formatNumber(data?.ordersToday),
			icon: ShoppingBag,
			description: "Commandes enregistrées aujourd'hui",
		},
		{
			title: "Chiffre d'affaires",
			value: formatCurrency(data?.revenue),
			icon: Wallet,
			description: "Montant total des ventes",
		},
		{
			title: "Produits actifs",
			value: formatNumber(data?.activeProducts),
			icon: Package,
			description: "Produits disponibles au catalogue",
		},
		{
			title: "Clients inscrits",
			value: formatNumber(data?.usersCount),
			icon: Users,
			description: "Comptes clients enregistrés",
		},
	];

	return (
		<div className="min-h-screen bg-[#F8F6F2]">
			<div className="mx-auto max-w-7xl px-4 pb-16 pt-28 sm:px-6 sm:pt-32 lg:px-8">
				{/* EN-TÊTE */}
				<div className="mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
					<div>
						<div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#A17B4D]">
							<Coffee size={16} />
							Léger's Coffee
							<span className="text-[#D3C5B4]">/</span>
							Administration
						</div>

						<h1 className="text-3xl font-bold tracking-tight text-[#30251E] sm:text-4xl">
							Vue d'ensemble
						</h1>

						<p className="mt-2 text-sm leading-relaxed text-[#82766B] sm:text-base">
							Bonjour{" "}
							<span className="font-semibold text-[#49392D]">
								{profile?.first_name || "Admin"}
							</span>
							, bienvenue dans votre espace de gestion.
						</p>

						<p className="mt-2 flex items-center gap-2 text-xs capitalize text-[#A29689]">
							<CalendarDays size={14} />
							{today}
						</p>
					</div>

					<button
						type="button"
						onClick={() => loadStats(true)}
						disabled={loading || refreshing}
						className="inline-flex w-fit items-center justify-center gap-2 rounded-xl border border-[#E6DED2] bg-white px-4 py-3 text-sm font-semibold text-[#49392D] shadow-sm transition hover:bg-[#F1EBE2] disabled:cursor-not-allowed disabled:opacity-50"
					>
						<RefreshCw size={17} className={refreshing ? "animate-spin" : ""} />
						{refreshing ? "Actualisation..." : "Actualiser"}
					</button>
				</div>

				{/* BANNIÈRE */}
				<div className="relative mb-8 overflow-hidden rounded-[26px] bg-[#30251E] px-6 py-7 text-white shadow-lg sm:px-9 sm:py-9">
					<div className="pointer-events-none absolute -right-12 -top-24 h-72 w-72 rounded-full border border-white/10" />
					<div className="pointer-events-none absolute -right-24 -top-12 h-72 w-72 rounded-full border border-white/10" />
					<div className="pointer-events-none absolute bottom-0 right-8 text-white/5">
						<Coffee size={190} strokeWidth={1} />
					</div>

					<div className="relative z-10 max-w-xl">
						<div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#BFA47D]/30 bg-white/10 px-3 py-1.5 text-xs font-medium text-[#E9CEAA]">
							<CheckCircle2 size={14} />
							Espace administrateur
						</div>

						<h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
							Pilotez votre boutique en toute simplicité.
						</h2>

						<p className="mt-3 text-sm leading-relaxed text-[#D2C5B8]">
							Retrouvez vos indicateurs essentiels et accédez rapidement aux
							outils de gestion de Léger's Coffee.
						</p>

						<Link
							to="/admin/orders"
							className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#D5B382] px-5 py-3 text-sm font-semibold text-[#30251E] transition hover:bg-[#E4C99F]"
						>
							Voir les commandes
							<ArrowRight size={17} />
						</Link>
					</div>
				</div>

				{/* ERREUR */}
				{error && (
					<div
						role="alert"
						className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
					>
						<div className="flex items-center gap-2">
							<AlertCircle size={18} />
							<span>{error}</span>
						</div>

						<button
							type="button"
							onClick={() => loadStats(true)}
							className="font-semibold underline underline-offset-4"
						>
							Réessayer
						</button>
					</div>
				)}

				{/* STATISTIQUES */}
				<section className="mb-10">
					<div className="mb-5 flex flex-wrap items-center justify-between gap-3">
						<div>
							<h2 className="text-xl font-bold text-[#30251E] sm:text-2xl">
								Statistiques
							</h2>
							<p className="mt-1 text-sm text-[#91867B]">
								Les indicateurs clés de votre activité.
							</p>
						</div>

						<div className="flex items-center gap-2 rounded-full border border-[#E8E0D5] bg-white px-3 py-2 text-xs text-[#82766B]">
							<Clock3 size={14} />
							Actualisation manuelle
						</div>
					</div>

					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
						{stats.map((stat) => (
							<StatCard key={stat.title} {...stat} loading={loading} />
						))}
					</div>
				</section>

				{/* GESTION */}
				<section className="mb-10">
					<div className="mb-5">
						<h2 className="text-xl font-bold text-[#30251E] sm:text-2xl">
							Gestion de la boutique
						</h2>
						<p className="mt-1 text-sm text-[#91867B]">
							Accédez rapidement à vos différents espaces.
						</p>
					</div>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
						{quickLinks.map((link) => (
							<QuickLinkCard key={link.to} {...link} />
						))}
					</div>
				</section>

				{/* ACTION RAPIDE */}
				<section>
					<div className="flex flex-col items-start justify-between gap-5 rounded-[24px] border border-[#E8DECF] bg-[#F1E8DC] p-6 sm:flex-row sm:items-center sm:p-8">
						<div className="flex items-start gap-4">
							<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-[#9E794B] shadow-sm">
								<TrendingUp size={23} />
							</div>

							<div>
								<h2 className="text-lg font-bold text-[#30251E]">
									Enrichissez votre catalogue
								</h2>

								<p className="mt-1 max-w-xl text-sm leading-relaxed text-[#82766B]">
									Ajoutez de nouveaux cafés, sandwichs, pâtisseries ou autres
									spécialités à votre boutique.
								</p>
							</div>
						</div>

						<Link
							to="/admin/products/new"
							className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#30251E] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#49382C]"
						>
							<Plus size={18} />
							Ajouter un produit
						</Link>
					</div>
				</section>

				{/* PIED DE PAGE */}
				<div className="mt-10 flex flex-col justify-between gap-2 border-t border-[#E9E2D8] pt-6 text-xs text-[#A29689] sm:flex-row">
					<span>© {new Date().getFullYear()} Léger's Coffee</span>
					<span>Interface d'administration</span>
				</div>
			</div>
		</div>
	);
};

export default AdminDashboardPage;
